import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';

const API_BASE = process.env.REACT_APP_BACKEND_URL + '/api';
const DEMO_EMAIL = 'Demo@demo.com';

const DemoContext = createContext(null);

export const useDemo = () => {
  const ctx = useContext(DemoContext);
  return ctx || { isDemo: false };
};

// -------------------------------------------------------------
// Step trigger rules — each rule maps a successful axios response
// to the step the demo should advance to.
//   { test: (cfg) => bool, toStep: number }
// `cfg` is the axios request config: { method, url, data }.
//
// Step map (12 walkthrough steps + 1 free-play):
//   1 Add Client          POST   /api/clients              → step 2
//   2 Edit Client         PUT    /api/clients/{id}         → step 3
//   3 Upload Docs         POST   /api/clients/{id}/documents → step 4
//   4 Approve & Terms     POST   /api/clients/{id}/offers OR /approve → step 5
//   5 Visit Submitted     route  /submitted-deals          → step 6 (DemoRouteWatcher)
//   6 Fund Deal           POST   /api/deals/{id}/fund      → step 7
//   7 Tour Funded chart   manual "Got it, next →" button   → step 8
//   8 Open Deal           manual after route /deals/{id}    → step 9
//   9 Add Syndicator User POST   /api/users                → step 10
//  10 Attach Syndicator   POST   /api/deals/{id}/syndicators → step 11
//  11 Clear 5 Payments    5x PUT /api/deals/{id}/payments/{n}/clear → step 12
//  12 Refinance           POST   /api/deals/{id}/refinance → step 13 (free play)
// -------------------------------------------------------------
const STEP_TRIGGERS = [
  // Step 1 → 2 (Add Client). Match POST to /api/clients root only, not sub-resources.
  {
    test: (cfg) => cfg.method === 'post' && /\/api\/clients\/?(\?.*)?$/.test(cfg.url || ''),
    toStep: 2,
  },
  // Step 2 → 3 (Edit Client). Match PUT to /api/clients/{id} but NOT /clients/{id}/owners etc.
  {
    test: (cfg) => cfg.method === 'put' && /\/api\/clients\/\d+\/?(\?.*)?$/.test(cfg.url || ''),
    toStep: 3,
  },
  // Step 3 → 4 (Upload Documents).
  {
    test: (cfg) => cfg.method === 'post' && /\/api\/clients\/\d+\/documents/.test(cfg.url || ''),
    toStep: 4,
  },
  // Step 4 → 5 (Approve & Build Terms). Either endpoint counts.
  {
    test: (cfg) => cfg.method === 'post' && (
      /\/api\/clients\/\d+\/offers/.test(cfg.url || '') ||
      /\/api\/clients\/\d+\/approve/.test(cfg.url || '')
    ),
    toStep: 5,
  },
  // Step 6 → 7 (Fund Deal).
  {
    test: (cfg) => cfg.method === 'post' && /\/api\/deals\/\d+\/fund\/?(\?.*)?$/.test(cfg.url || ''),
    toStep: 7,
  },
  // Step 9 → 10 (Add Syndicator user via /api/users).
  {
    test: (cfg) => cfg.method === 'post' && /\/api\/users\/?(\?.*)?$/.test(cfg.url || ''),
    toStep: 10,
  },
  // Step 10 → 11 (Attach Syndicator to deal).
  {
    test: (cfg) => cfg.method === 'post' && /\/api\/deals\/\d+\/syndicators\/?(\?.*)?$/.test(cfg.url || ''),
    toStep: 11,
  },
  // Step 12 → 13 (Refinance → Free play).
  {
    test: (cfg) => cfg.method === 'post' && /\/api\/deals\/\d+\/refinance/.test(cfg.url || ''),
    toStep: 13,
  },
];

// Step 6 → 7 happens via route change to /submitted-deals (handled in the
// component that observes `useLocation`, see useDemoRouteWatcher below).
// Step 8 → 9 happens when the user opens DealDetail.
// Step 12 → 13 needs counting cleared payments — handled separately.

/**
 * DemoProvider — wraps the authenticated app shell.
 *
 * Responsibilities:
 *  - Detect when the logged-in user is the Demo user.
 *  - Hydrate walkthrough config + current step + remaining seconds from /api/demo/*.
 *  - Tick the countdown every second (without spamming the backend).
 *  - Auto-end the demo (logout + reload) when the timer hits zero.
 *  - Expose advance(toStep) so individual pages can trigger step advances on user action.
 */
export const DemoProvider = ({ children }) => {
  const { user, logout } = useAuth();
  const isDemo = user?.email === DEMO_EMAIL;

  const [config, setConfig] = useState(null);             // { steps:[…], total_steps, duration_minutes }
  const [step, setStep] = useState(0);                    // current step id (1..N)
  const [remainingSeconds, setRemainingSeconds] = useState(0);
  const [active, setActive] = useState(false);
  const [welcomeOpen, setWelcomeOpen] = useState(false);  // first-login modal
  const [expiredOpen, setExpiredOpen] = useState(false);  // session-expired modal

  const tickRef = useRef(null);
  const expiredHandledRef = useRef(false);
  const interceptorRef = useRef(null);
  const stepRef = useRef(0);
  const clearedCountRef = useRef(0);

  // Keep stepRef in sync (interceptor closures need fresh value)
  useEffect(() => { stepRef.current = step; }, [step]);

  const authHeaders = useCallback(() => {
    const token = localStorage.getItem('mcaToken');
    return token ? { Authorization: `Bearer ${token}` } : {};
  }, []);

  // -------------------------------------------------------------
  // Initial hydrate on demo login
  // -------------------------------------------------------------
  useEffect(() => {
    if (!isDemo) return;

    let cancelled = false;
    (async () => {
      try {
        // Fetch step catalog (public)
        const cfgRes = await axios.get(`${API_BASE}/demo/config`);
        if (cancelled) return;
        setConfig(cfgRes.data);

        // Try existing status — if no active session, start one
        let statusRes;
        try {
          statusRes = await axios.get(`${API_BASE}/demo/status`, { headers: authHeaders() });
        } catch {
          statusRes = { data: { active: false } };
        }
        if (!statusRes.data?.active) {
          const startRes = await axios.post(`${API_BASE}/demo/start`, {}, { headers: authHeaders() });
          if (cancelled) return;
          setStep(startRes.data.step || 1);
          setRemainingSeconds(startRes.data.remaining_seconds || 1200);
          setActive(true);
          if (!startRes.data.resumed) setWelcomeOpen(true);
        } else {
          setStep(statusRes.data.step || 1);
          setRemainingSeconds(statusRes.data.remaining_seconds || 0);
          setActive(true);
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('Demo hydrate failed:', err);
      }
    })();

    return () => { cancelled = true; };
  }, [isDemo, authHeaders]);

  // -------------------------------------------------------------
  // Local 1-second tick (no backend call)
  // -------------------------------------------------------------
  useEffect(() => {
    if (!isDemo || !active) return;
    tickRef.current = setInterval(() => {
      setRemainingSeconds((prev) => Math.max(0, prev - 1));
    }, 1000);
    return () => clearInterval(tickRef.current);
  }, [isDemo, active]);

  // -------------------------------------------------------------
  // Sync with backend every 30s (drift correction + cross-tab safety)
  // -------------------------------------------------------------
  useEffect(() => {
    if (!isDemo || !active) return;
    const id = setInterval(async () => {
      try {
        const res = await axios.get(`${API_BASE}/demo/status`, { headers: authHeaders() });
        if (res.data?.expired) {
          handleExpiry();
          return;
        }
        if (res.data?.active) {
          setRemainingSeconds(res.data.remaining_seconds || 0);
          setStep((cur) => Math.max(cur, res.data.step || 1));
        }
      } catch { /* silent */ }
    }, 30000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDemo, active]);

  // -------------------------------------------------------------
  // Expiry handling
  // -------------------------------------------------------------
  const handleExpiry = useCallback(() => {
    if (expiredHandledRef.current) return;
    expiredHandledRef.current = true;
    setActive(false);
    setExpiredOpen(true);
    // Wipe + log out after the user sees the modal
    axios.post(`${API_BASE}/demo/finish`, {}, { headers: authHeaders() }).catch(() => {});
    setTimeout(() => {
      logout();
      window.location.replace('/login?demo_expired=1');
    }, 4000);
  }, [authHeaders, logout]);

  useEffect(() => {
    if (isDemo && active && remainingSeconds === 0) {
      handleExpiry();
    }
  }, [isDemo, active, remainingSeconds, handleExpiry]);

  // -------------------------------------------------------------
  // advance(toStep) — fire-and-forget, idempotent
  // -------------------------------------------------------------
  const advance = useCallback(async (toStep) => {
    if (!isDemo || !active) return;
    if (toStep && toStep <= stepRef.current) return;  // never regress
    try {
      const res = await axios.post(
        `${API_BASE}/demo/advance`,
        toStep ? { to_step: toStep } : {},
        { headers: authHeaders() }
      );
      setStep(res.data.step);
    } catch { /* silent */ }
  }, [isDemo, active, authHeaders]);

  // -------------------------------------------------------------
  // Global axios response interceptor — auto-advance on key actions
  // -------------------------------------------------------------
  useEffect(() => {
    if (!isDemo || !active) return;

    const id = axios.interceptors.response.use(
      (response) => {
        try {
          const cfg = response.config || {};
          const url = cfg.url || '';
          // Don't recurse — ignore demo endpoints
          if (url.includes('/api/demo/')) return response;

          const method = (cfg.method || 'get').toLowerCase();
          const reqCfg = { method, url, data: cfg.data };

          // Special: count cleared payments to fire step 11 → 12
          if (
            method === 'put' &&
            /\/api\/deals\/\d+\/payments\/\d+\/clear/.test(url) &&
            stepRef.current >= 10 && stepRef.current < 12
          ) {
            clearedCountRef.current += 1;
            if (clearedCountRef.current >= 5) {
              advance(12);
            }
          }

          // Generic triggers
          for (const rule of STEP_TRIGGERS) {
            try {
              if (rule.test(reqCfg)) {
                advance(rule.toStep);
                break;
              }
            } catch { /* keep going */ }
          }
        } catch { /* never break the response */ }
        return response;
      },
      (error) => Promise.reject(error)
    );

    interceptorRef.current = id;
    return () => {
      if (interceptorRef.current !== null) {
        axios.interceptors.response.eject(interceptorRef.current);
        interceptorRef.current = null;
      }
    };
  }, [isDemo, active, advance]);

  // -------------------------------------------------------------
  // Manual finish — wipes data, logs out, hard-redirects to /login
  // -------------------------------------------------------------
  const finish = useCallback(async () => {
    if (!isDemo) return;
    // Fire the wipe; even if it errors, we still log out + redirect.
    try {
      await axios.post(`${API_BASE}/demo/finish`, {}, { headers: authHeaders() });
    } catch { /* silent */ }
    // Belt-and-suspenders: clear storage manually before logout in case
    // the AuthContext logout call gets unmounted mid-flow.
    try {
      localStorage.removeItem('mcaToken');
      localStorage.removeItem('mcaUser');
      sessionStorage.removeItem('mcaToken');
      sessionStorage.removeItem('mcaUser');
    } catch { /* silent */ }
    try { logout(); } catch { /* silent */ }
    // Force a hard navigation so React state, axios interceptors, and any
    // mounted modals are torn down.
    window.location.href = '/login?demo_ended=1';
  }, [isDemo, authHeaders, logout]);

  // -------------------------------------------------------------
  // Convenience derivations
  // -------------------------------------------------------------
  const currentStep = config?.steps?.find((s) => s.id === step) || null;
  const minutes = Math.floor(remainingSeconds / 60);
  const seconds = remainingSeconds % 60;
  const timer = `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;

  const value = {
    isDemo,
    active,
    step,
    totalSteps: config?.total_steps || 0,
    currentStep,
    remainingSeconds,
    timer,
    welcomeOpen,
    setWelcomeOpen,
    expiredOpen,
    setExpiredOpen,
    advance,
    finish,
    config,
  };

  return <DemoContext.Provider value={value}>{children}</DemoContext.Provider>;
};

export default DemoContext;
